import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface SubscriptionInfo {
  id: string;
  plan_name: string;
  plan_features: Record<string, boolean>;
  licensed_locations: number;
  licensed_registers: number;
  status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'unpaid';
  current_period_end: string | null;
  trial_ends_at: string | null;
  last_synced_at: string;
  billing_interval?: 'monthly' | 'annual';
}

export interface LicenseUsage {
  active_locations: number;
  active_registers: number;
  licensed_locations: number;
  licensed_registers: number;
  plan_name: string;
  status: string;
  locations_available: number;
  registers_available: number;
}

export interface PlanPricing {
  plan_name: string;
  base_price: number;
  per_location_price: number;
  per_register_price: number;
  annual_base_price: number;
  annual_per_location_price: number;
  annual_per_register_price: number;
  unlimited_locations: boolean;
  unlimited_registers: boolean;
}

// Pricing defaults based on plan (fallback if API unavailable)
// Aligned with database migration 20240003000000_update_pricing_tiers.sql
const PLAN_PRICING_DEFAULTS: Record<string, PlanPricing> = {
  Entry: {
    plan_name: 'Entry',
    base_price: 45.00,
    per_location_price: 45.00,
    per_register_price: 30.00,
    annual_base_price: 460.00,
    annual_per_location_price: 460.00,
    annual_per_register_price: 288.00,
    unlimited_locations: false,
    unlimited_registers: false,
  },
  Core: {
    plan_name: 'Core',
    base_price: 75.00,
    per_location_price: 75.00,
    per_register_price: 30.00,
    annual_base_price: 720.00,
    annual_per_location_price: 720.00,
    annual_per_register_price: 288.00,
    unlimited_locations: false,
    unlimited_registers: false,
  },
  Pro: {
    plan_name: 'Pro',
    base_price: 145.00,
    per_location_price: 0,
    per_register_price: 0,
    annual_base_price: 1109.76,
    annual_per_location_price: 0,
    annual_per_register_price: 0,
    unlimited_locations: true,
    unlimited_registers: true,
  },
  // Legacy plan names (backwards compatibility)
  Starter: {
    plan_name: 'Entry',
    base_price: 45.00,
    per_location_price: 45.00,
    per_register_price: 30.00,
    annual_base_price: 460.00,
    annual_per_location_price: 460.00,
    annual_per_register_price: 288.00,
    unlimited_locations: false,
    unlimited_registers: false,
  },
};

export function useSubscriptionInfo() {
  return useQuery({
    queryKey: ['subscription-info'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_info')
        .select('*')
        .limit(1)
        .single();

      if (error) {
        // If table doesn't exist yet, return default values
        if (error.code === 'PGRST116' || error.code === '42P01') {
          return {
            id: 'default',
            plan_name: 'Entry',
            plan_features: {},
            licensed_locations: 1,
            licensed_registers: 1,
            status: 'active' as const,
            current_period_end: null,
            trial_ends_at: null,
            last_synced_at: new Date().toISOString(),
            billing_interval: 'monthly' as const,
          } as SubscriptionInfo;
        }
        throw error;
      }

      return data as SubscriptionInfo;
    },
  });
}

export function useLicenseUsage() {
  return useQuery({
    queryKey: ['license-usage'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_license_usage');

      if (error) {
        // If function doesn't exist, calculate manually
        if (error.code === '42883') {
          const [locationsResult, registersResult, subscriptionResult] = await Promise.all([
            supabase.from('locations').select('id', { count: 'exact', head: true }).eq('is_active', true),
            supabase.from('registers').select('id', { count: 'exact', head: true }).eq('is_active', true),
            supabase.from('subscription_info').select('*').limit(1).single(),
          ]);

          const subscription = subscriptionResult.data || {
            licensed_locations: 1,
            licensed_registers: 1,
            plan_name: 'Entry',
            status: 'active',
          };

          return {
            active_locations: locationsResult.count || 0,
            active_registers: registersResult.count || 0,
            licensed_locations: subscription.licensed_locations,
            licensed_registers: subscription.licensed_registers,
            plan_name: subscription.plan_name,
            status: subscription.status,
            locations_available: subscription.licensed_locations - (locationsResult.count || 0),
            registers_available: subscription.licensed_registers - (registersResult.count || 0),
          } as LicenseUsage;
        }
        throw error;
      }

      return (data?.[0] || {
        active_locations: 0,
        active_registers: 0,
        licensed_locations: 1,
        licensed_registers: 1,
        plan_name: 'Entry',
        status: 'active',
        locations_available: 1,
        registers_available: 1,
      }) as LicenseUsage;
    },
  });
}

// Get pricing information for the current plan
export function usePlanPricing() {
  const { data: subscription } = useSubscriptionInfo();
  
  return useQuery({
    queryKey: ['plan-pricing', subscription?.plan_name],
    queryFn: async () => {
      const planName = subscription?.plan_name || 'Entry';
      
      // Return defaults based on plan name
      // In production, this could fetch from admin API for dynamic pricing
      return PLAN_PRICING_DEFAULTS[planName] || PLAN_PRICING_DEFAULTS.Entry;
    },
    enabled: !!subscription,
  });
}

export function useCanAddResource(resourceType: 'location' | 'register') {
  return useQuery({
    queryKey: ['can-add-resource', resourceType],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('can_add_resource', {
        p_resource_type: resourceType,
      });

      if (error) {
        // If function doesn't exist, use manual check
        if (error.code === '42883') {
          const licenseUsage = await supabase.rpc('get_license_usage');
          const usage = licenseUsage.data?.[0];
          
          if (!usage) {
            return { can_add: true, current_count: 0, licensed_count: 10, message: null };
          }

          if (resourceType === 'location') {
            return {
              can_add: usage.active_locations < usage.licensed_locations,
              current_count: usage.active_locations,
              licensed_count: usage.licensed_locations,
              message: usage.active_locations >= usage.licensed_locations
                ? `You have reached your location limit (${usage.active_locations}/${usage.licensed_locations}). Please upgrade your plan.`
                : null,
            };
          } else {
            return {
              can_add: usage.active_registers < usage.licensed_registers,
              current_count: usage.active_registers,
              licensed_count: usage.licensed_registers,
              message: usage.active_registers >= usage.licensed_registers
                ? `You have reached your register limit (${usage.active_registers}/${usage.licensed_registers}). Please upgrade your plan.`
                : null,
            };
          }
        }
        throw error;
      }

      return data?.[0] || { can_add: true, current_count: 0, licensed_count: 10, message: null };
    },
  });
}

// Admin Supabase Functions URL (for edge functions like billing-portal, update-license)
// This should point to the admin Supabase project's functions endpoint
const ADMIN_FUNCTIONS_URL = import.meta.env.VITE_ADMIN_FUNCTIONS_URL || 'https://nwzibkuwrcwqnmnytiaj.supabase.co';
const ADMIN_ANON_KEY = import.meta.env.VITE_ADMIN_ANON_KEY || '';

// Helper to get tenant slug
function getTenantSlug(): string {
  const hostname = window.location.hostname;

  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    const parts = hostname.split('.');
    // For subdomains like client-slug.blazepos.app (3 parts)
    // or client-slug.blazepos.pages.dev (4 parts)
    if (parts.length >= 3) {
      return parts[0];
    }
  }

  return localStorage.getItem('dev_tenant_slug') || 'dev';
}

// Self-service license change (add/remove locations or registers)
export function useLicenseChange() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      resourceType,
      quantity,
    }: {
      resourceType: 'location' | 'register';
      quantity: number;
    }) => {
      // Get the current user's session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated. Please sign in to manage your license.');
      }

      const slug = getTenantSlug();

      const response = await fetch(`${ADMIN_FUNCTIONS_URL}/functions/v1/update-license`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': ADMIN_ANON_KEY,
        },
        body: JSON.stringify({
          client_slug: slug,
          resource_type: resourceType,
          quantity,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to update license' }));
        throw new Error(error.error || error.message || 'Failed to update license');
      }

      return response.json();
    },
    onSuccess: () => {
      // Refresh all license-related queries
      queryClient.invalidateQueries({ queryKey: ['subscription-info'] });
      queryClient.invalidateQueries({ queryKey: ['license-usage'] });
      queryClient.invalidateQueries({ queryKey: ['can-add-resource'] });
      queryClient.invalidateQueries({ queryKey: ['plan-pricing'] });
    },
  });
}

// Legacy hook name for backwards compatibility
export function useRequestLicenseIncrease() {
  return useLicenseChange();
}

// Helper to get the org slug from the authenticated user's profile
async function getOrgSlug(): Promise<string> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('organization:organizations(slug)')
    .eq('id', (await supabase.auth.getUser()).data.user?.id || '')
    .single();

  const slug = (profile?.organization as any)?.slug;
  if (slug) return slug;

  // Fallback to hostname/localStorage detection
  return getTenantSlug();
}

// Ensure client exists in admin DB (auto-register if missing)
async function ensureAdminClient(session: any, _slug: string): Promise<void> {
  if (!ADMIN_FUNCTIONS_URL) return;

  // Try to register -- the function is idempotent (checks if already exists)
  const { data: profile } = await supabase
    .from('profiles')
    .select('first_name, last_name, organization:organizations(name, slug, id)')
    .eq('id', session.user.id)
    .single();

  const org = profile?.organization as any;
  if (!org) return;

  try {
    await fetch(`${ADMIN_FUNCTIONS_URL}/functions/v1/register-trial`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': ADMIN_ANON_KEY,
      },
      body: JSON.stringify({
        organization_name: org.name,
        organization_slug: org.slug,
        organization_id: org.id,
        email: session.user.email,
        first_name: profile?.first_name || '',
        last_name: profile?.last_name || '',
      }),
    });
  } catch {
    // Non-blocking
  }
}

// Start Stripe Checkout for plan subscription (trial-to-paid conversion)
export function useStartCheckout() {
  return useMutation({
    mutationFn: async ({
      planName,
      billingInterval = 'monthly',
    }: {
      planName: string;
      billingInterval?: 'monthly' | 'annual';
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated. Please sign in to subscribe.');
      }

      const slug = await getOrgSlug();

      // Ensure the client record exists in admin DB before checkout
      await ensureAdminClient(session, slug);

      const checkoutBody = JSON.stringify({
        client_slug: slug,
        plan_name: planName,
        billing_interval: billingInterval,
        success_url: `${window.location.origin}/settings?checkout=success`,
        cancel_url: `${window.location.origin}/settings?checkout=cancelled`,
      });

      const checkoutQuery = new URLSearchParams({
        client_slug: slug,
        plan_name: planName,
        billing_interval: billingInterval,
        success_url: `${window.location.origin}/settings?checkout=success`,
        cancel_url: `${window.location.origin}/settings?checkout=cancelled`,
      });

      const response = await fetch(`${ADMIN_FUNCTIONS_URL}/functions/v1/create-checkout?${checkoutQuery.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': ADMIN_ANON_KEY,
        },
        body: checkoutBody,
        cache: 'no-store',
        redirect: 'follow',
      });

      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch {
        throw new Error('Unexpected response from checkout service');
      }

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Failed to create checkout session');
      }

      if (!result.url) {
        throw new Error('No checkout URL returned. Stripe may not be configured yet.');
      }

      window.location.href = result.url;
    },
  });
}

// Open Stripe customer portal
export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: async () => {
      // Get the current user's session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated. Please sign in to access billing.');
      }

      const slug = getTenantSlug();

      const response = await fetch(`${ADMIN_FUNCTIONS_URL}/functions/v1/billing-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': ADMIN_ANON_KEY,
        },
        body: JSON.stringify({
          client_slug: slug,
          return_url: window.location.href,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to create billing portal session' }));
        throw new Error(error.error || error.message || 'Failed to create billing portal session');
      }

      const { url } = await response.json();
      window.location.href = url;
    },
  });
}
